import { IPromise } from 'angular';
import { Promise } from 'es6-promise';
import { put, PutEffect } from 'redux-saga/effects';

import { Messages } from '../../../../codegen/Localize';
import { ListResult } from '../../../../ts/fetchers/ListResult';
import { Filter } from '../../../../ts/helpers/Filter';
import { SliceableHelper } from '../../../../ts/helpers/SliceableHelper';
import {
  DatasourceDefinition,
  ProjectDefinition,
  ViewDefinition,
  WorkbookDefinition,
} from '../../../../ts/models/ContentDefinitions';
import { getKeyForExplorableItem } from '../../../../ts/models/ContentType';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IDataConnectionInfo } from '../../../../ts/models/IDataConnectionInfo';
import { IDataSourceDetailInfo, IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';
import { IViewDetailInfo } from '../../../../ts/models/IViewInfo';
import { IWorkbookInfo } from '../../../../ts/models/IWorkbookInfo';
import { IExplorableItemTypeInfo } from '../../../../ts/models/resourceInfoParts';
import { DataConnections } from '../../../../ts/resources/DataConnections';
import { DataSources } from '../../../../ts/resources/Datasources';
import { Views } from '../../../../ts/resources/Views';
import { Workbooks } from '../../../../ts/resources/Workbooks';
import { FilterFieldNames } from '../../../../ts/ServerApi/types/IFilterClause';
import { requestFailed, RequestFailedAction } from '../../../actions/RequestFailedAction';
import {
  GetContentDetailAction,
  updateContentDetail,
  UpdateContentDetailAction,
} from '../../../actions/spaces/GetContentDetailAction';
import { ServiceLocator } from '../../../ServiceLocator';

export function* processGetContentDetailAction(action: GetContentDetailAction) {
  try {
    switch(action.content.contentType){
      case ProjectDefinition.contentType:
        break;
      case WorkbookDefinition.contentType:
        let workbookContent = null;
        if (!Workbooks.isWorkbookInfo(action.content)) {
          // getting workbook info instead of workbook detail info, because numSheets does not exist in workbook detail info
          workbookContent = getWorkbookInfo(action.content);
        }

        let datasourceList = null;
        if(!action.content.hasOwnProperty('datasources')){
          datasourceList = getDatasourcesForWorkbook(action.content.id);
        }

        let updatedContent: any = null;
        yield Promise.all([workbookContent, datasourceList]).then(function(results){
          if(results[0]){
            updatedContent = results[0];
          }
          if(results[1]){
            updatedContent = updatedContent || action.content;
            updatedContent = { ...updatedContent, datasources: results[1].items };
          }
        });
        //if anything has changed, update the react state
        if(updatedContent){
          yield put(updateContentDetail(updatedContent));
        }
        break;
      case ViewDefinition.contentType:
        if (!Views.isViewDetailInfo(action.content)) {
          const viewDetailInfo = yield getViewDetailInfo(action.content);
          yield put(updateContentDetail(viewDetailInfo));
        }
        break;
      case DatasourceDefinition.contentType:
        const ds = action.content as any;
        if (!ds.connections) {
          const connections = yield getDataConnections(action.content);
          const updatedContent = { ...action.content, connections };
          yield put(updateContentDetail(updatedContent));
        }
        break;
    }
  }
  catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getViewDetailInfo(content: IExplorableItemTypeInfo): IPromise<IViewDetailInfo> {
  const resource = ServiceLocator.get<Views>('Views');
  return resource.getById(content.id);
}

function getWorkbookInfo(content: IExplorableItemTypeInfo): IPromise<IWorkbookInfo> {
  const resource = ServiceLocator.get<Workbooks>('Workbooks');
  return resource.all().addFilter(Filter.stringFieldEq('id', content.id)).getSlice(0, 1).then(result => result.items[0]);
}

function getDataConnections(content: IExplorableItemTypeInfo): IPromise<IDataConnectionInfo[]> {
  const datasources = ServiceLocator.get<DataSources>('DataSources');
  return datasources.fromContent(content).then(datasourceInfos => {
    if (datasourceInfos.length > 0) {
      const connections = ServiceLocator.get<DataConnections>('DataConnections');
      return connections.getFromDatasource(datasourceInfos[0]);
    } else {
      return [];
    }
  });
}

function getDatasourcesForWorkbook(workbookId: string): IPromise<ListResult<IDataSourceInfo>> {
  const datasources = ServiceLocator.get<DataSources>('DataSources');
  return datasources.getByWorkbookId(workbookId);
}
